/**
 * Card display-masking for the sign-up "card on file" (E-SF05, no-Stripe capture).
 *
 * PURE. Takes the raw card number + expiry the customer typed and returns ONLY the
 * display-safe masked descriptor `{ brand, last4, expMonth, expYear }`. The full PAN
 * is never returned, transmitted, or stored — the caller sends only the masked
 * result to the BFF and clears the raw input immediately. There is no CVV: the form
 * does not ask for one (see `cardEntry.ts`).
 *
 * ── SAME RULES AND SAME BRAND NAMES AS THE PORTAL AND THE CRM ───────────────
 * The checks are `cardEntry.ts`'s — the ones the Customer Portal's Add card and the
 * CRM's Payment methods panel run — and `brand` is their spelling (`Visa`,
 * `American Express`, …). This descriptor ends up as a row in billing's
 * `payment_method` table once the account exists, beside cards an agent or the
 * customer added there. One spelling is what lets the CRM recognise the sign-up
 * card as the same card instead of listing it twice.
 *
 * ⚠️ Deriving `last4`/`brand` means OUR JS briefly sees the PAN (unlike Stripe's
 * hosted iframe). Acceptable for this capture, which stores masked fields only;
 * real card collection must use Stripe Elements so the PAN never touches our page.
 */

import { checkCardNumber, checkExpiry, expiryParts } from './cardEntry';

export interface MaskedCard {
  readonly brand: string;
  readonly last4: string;
  readonly expMonth: number;
  readonly expYear: number;
}

/**
 * Which field is wrong, and what to tell the customer about it. Per field, because
 * one blanket "that card doesn't look right" sends them re-reading a number that
 * was never the problem.
 */
export interface MaskedCardErrors {
  readonly number?: string;
  readonly expiry?: string;
}

export type DeriveMaskedCardResult = MaskedCard | { readonly errors: MaskedCardErrors };

/**
 * Derive the masked descriptor from the typed card, or the per-field errors.
 * `now` is a parameter so "has it expired" can be asked about a fixed moment in a
 * test. NEVER returns the full number.
 */
export function deriveMaskedCard(
  rawNumber: string,
  rawExpiry: string,
  now: Date = new Date(),
): DeriveMaskedCardResult {
  const card = checkCardNumber(rawNumber);
  const expiry = checkExpiry(rawExpiry, now);
  const parts = expiryParts(rawExpiry);

  if (!card.ok || !expiry.ok || parts === null) {
    return {
      errors: {
        ...(card.ok ? {} : { number: card.error }),
        ...(expiry.ok ? {} : { expiry: expiry.error }),
      },
    };
  }
  return {
    brand: card.brand,
    last4: card.last4,
    expMonth: parts.month,
    expYear: parts.year,
  };
}
